import React, { useState, useCallback, useEffect } from 'react';
import {
  Content,
  Header,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Grid,
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  makeStyles,
  TextField,
  TablePagination,
  Button,
  useTheme,
  InputAdornment,
  Box,
  Typography
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import axios from 'axios';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { configApiRef } from '@backstage/core-plugin-api';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { displayedRowsLabel } from '../../i18n/pagination';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    maxHeight: 400,
  },
  tableHeader: {
    backgroundColor: '#fff', // White header background
    fontWeight: 'bold',
  },
  dormantRow: {
    backgroundColor: '#fff3bf',
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'flex-end', // Align to the right
    marginBottom: theme.spacing(2),
  },
  searchBar: {
    width: '300px', // Fixed width for the search bar
  },
  backButton: {
    marginBottom: theme.spacing(2),
    width:'15%'
  },
  paginationRoot: {
    color: theme.palette.text.primary,
    '& .MuiTablePagination-selectRoot': {
      color: theme.palette.text.primary,
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-select': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-actions': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-caption': {
      color: theme.palette.text.primary,
    }
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between', // This will put items at the ends
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

interface TxFlow {
  flowId: number;
  ackedMsgCount: number;
  activationTime: number;
  activityState: string;
  bindTime: number;
  clientName: string;
  queueName: string;
}

interface QueueConsumersPageProps {
  txFlowsUri: string;
  queueName: string;
  onBack: () => void;
}

export const QueueConsumersPage: React.FC<QueueConsumersPageProps> = ({ txFlowsUri, onBack, queueName }) => {
  const { t, formatNumber, formatUtcDateTime } =
    useServiceRegistryTranslation();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [txFlows, setTxFlows] = useState<TxFlow[]>([]);
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef)
  const backendUrl = configApi.getString('backend.baseUrl');
  const theme = useTheme();
  
  // Pagination and Search State
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const isDormantConsumer = (bindTime: number) => {
    const bindDate = new Date(bindTime * 1000);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - bindDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 1;
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { token } = await identityApi.getCredentials();
      const response = await axios.post(`${backendUrl}/api/queueDetails`,
        {
          queueName: txFlowsUri
        },
        {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data.data.map((queue: any) => ({
        flowId : queue.flowId,
        ackedMsgCount : queue.ackedMsgCount,
        activationTime : queue.activationTime,
        activityState : queue.activityState,
        bindTime : queue.bindTime,
        clientName : queue.clientName,
        queueName : queue.queueName
      }));
      setTxFlows(data);
    } catch (e) {
      console.error('Error loading data:', e);
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [txFlowsUri]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle pagination
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value.toLowerCase());
    setPage(0); // Reset to first page when searching
  };
  
  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Filtered data based on search
  const filteredData = txFlows.filter((row) =>
    Object.values(row).some((value) =>
      value.toString().toLowerCase().includes(searchText)
    )
  );

  // Paginated data
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Header title={queueName} type={t('queues.consumersType')} />
      <Content>
        <div className={classes.root}>
          <Button
            variant="contained"
            color="primary"
            className={classes.backButton}
            onClick={onBack}
          >
            {t('queues.backToList')}
          </Button>
          
          {isLoading && <Progress />}
          {error && <ResponseErrorPanel error={error} />}

          {!isLoading && !error && (
            <Grid item xs={12}>
              {/* Top container with search on the right */}
              <div className={classes.topContainer}>
                <div></div> {/* Empty div to push search to the right */}
                <div className={classes.searchContainer}>
                  <TextField
                    placeholder={t('queues.searchConsumers')}
                    variant="standard"
                    value={searchText}
                    onChange={handleSearch}
                    inputRef={searchInputRef}
                    onFocus={handleSearchFocus}
                    className={classes.searchBar}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
              
              <Card className={classes.card}>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell>{t('queues.consumerFlowId')}</TableCell>
                        <TableCell>{t('queues.messagesConfirmed')}</TableCell>
                        <TableCell>{t('queues.activationTimeUtc')}</TableCell>
                        <TableCell>{t('queues.consumerStatus')}</TableCell>
                        <TableCell>{t('queues.bindTimeUtc')}</TableCell>
                        <TableCell>{t('queues.clientName')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((row) => (
                        <TableRow
                          key={row.flowId}
                          className={isDormantConsumer(row.bindTime) ? classes.dormantRow : ''}
                        >
                          <TableCell>{formatNumber(row.flowId)}</TableCell>
                          <TableCell>{formatNumber(row.ackedMsgCount)}</TableCell>
                          <TableCell>
                            {formatUtcDateTime(row.activationTime * 1000, true)}
                          </TableCell>
                          <TableCell>{row.activityState}</TableCell>
                          <TableCell>
                            {formatUtcDateTime(row.bindTime * 1000, true)}
                          </TableCell>
                          <TableCell>{row.clientName}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]} 
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  labelRowsPerPage={t('pagination.rows')}
                  labelDisplayedRows={values => displayedRowsLabel(t, values)}
                  classes={{
                    root: classes.paginationRoot
                  }}
                />
              </Card>
            </Grid>
          )}
        </div>
      </Content>
    </>
  );
};
